import { z } from 'zod';

export const itemPackingSchema = z.enum([
  'pallet',
  'package',
  'unpackable_item', //  not able to pack the item because of it's size such as fridge big coffee machine. This usually comes with quantity 1
  'bag',
  'barrel',
  'other',
]);
export type ItemPacking = z.infer<typeof itemPackingSchema>;
/**
 * What is being shipped, as the person booking the load would say it.
 *
 * Deliberately coarser than a government cargo taxonomy: this drives icons,
 * the agent's `inferItemType`, Org/client defaults and temperature hints, all
 * of which want a short human list. It is NOT a mirror of Bayan's 173-node
 * tree — that lives in `@hero/apps` (`bayan.good-types.data.ts`) and is
 * fetched live, because it is ELM's taxonomy and changes on ELM's schedule.
 *
 * What the two DO share is that every value here resolves to exactly one
 * Bayan branch, so `BAYAN_GOOD_TYPE_FALLBACK` can pick a submittable leaf
 * without guessing. Adding a value here means adding its mapping there; the
 * `Record<ItemType, …>` makes that a compile error rather than a runtime
 * blocker at issue time.
 */
export const itemTypeSchema = z.enum([
  // General cargo
  'food',
  'beauty',
  'perfume',
  'clothing',
  'textiles',
  'furniture',
  'appliances',
  'electronics',
  'paper',
  'plastic',
  'cleaning_materials',

  // Health
  'medical_supplies',
  'medicines',

  // Industrial and bulk
  'construction_materials',
  'chemicals',
  'spare_parts',
  'agriculture',
  'livestock',
  'petroleum',
  'mining',
  'waste',
  'vehicles',

  /**
   * UN-regulated hazardous cargo. Distinct from `chemicals`, which is
   * ordinary chemical freight — detergent, fertiliser, paint. Only this value
   * maps into Bayan's Dangerous Materials branch, which additionally requires
   * the load's UN number (`OrderItem.dangerousCode`).
   */
  'dangerous_materials',

  /**
   * @deprecated A state, not a cargo category, and the Bayan node it mapped
   * to no longer exists. Kept so orders already carrying it still parse;
   * excluded from the pickers. Use `petroleum`, `food` or `chemicals`.
   */
  'liquid',

  'other',
]);
export type ItemType = z.infer<typeof itemTypeSchema>;
